import _ from "lodash";
import {
  Mode,
  EVENT_ACTIVITY_TYPE,
  ActivityCategories,
} from "us.common/constants";
import { handleEndPointLocals, trimText } from "us.common/functions";
import { IActivity } from "us.common/interfaces";
import { IGroupingOptions, Mode as IMode } from "../Interfaces";
import dayjs from "dayjs";

/**
 * @function
 * @description get suitable placeholder
 * @param {Mode} mode mode of activity execution bar
 * @param {boolean} isBulkExecutionEnabled
 * @param {boolean} hasActivities
 * @param {boolean} isFetchingActivities
 * @returns {string} place holder value
 */
export const getActivityPlaceholder = (
  mode: IMode,
  isBulkExecutionEnabled: boolean,
  hasActivities: boolean,
  isFetchingActivities: boolean
): string => {
  try {
    if (
      !isBulkExecutionEnabled &&
      !hasActivities &&
      !isFetchingActivities &&
      mode == Mode.Click
    ) {
      return handleEndPointLocals(
        "US.COMMON:ACTIVITY_EXECUTION.NO_HANDLING_ACTIVITIES_TO_EXECUTE"
      );
    } else {
      return handleEndPointLocals("US.COMMON:ACTIVITY_EXECUTION.RUN_ACTIVITY");
    }
  } catch (error) {
    return "";
  }
};

/**
 * @function
 * @description get select activity data
 * @param {Array<IActivity>} activities activities
 * @param {Number} activityId select activity
 * @returns {IActivity} select activity data
 */
export const getSelectedActivity = (
  activities: Array<IActivity>,
  activityId: number
): IActivity | undefined => {
  try {
    return activities.find((activity: IActivity) => {
      return activity.activityId === activityId;
    });
  } catch (error) {}
};

/**
 * @function
 * @description filter activity by acivity name or code
 * @param {Array<IActivity>} activities activities
 * @param {string} searchText -search value
 * @returns  {Array<IActivity>} filtered activities
 */
export const searchActivityByCodeOrName = (
  activities: Array<IActivity>,
  searchText: string
): Array<IActivity> => {
  try {
    return searchText.trim().length !== 0
      ? activities.filter((activity: IActivity) => {
          const { activityCode, displayName } = activity;
          return (
            activityCode.toLowerCase().includes(searchText.toLowerCase()) ||
            displayName.toLowerCase().includes(searchText.toLowerCase())
          );
        })
      : activities;
  } catch (error) {
    return [];
  }
};

/**
 * @function
 * @description get options without grouping for auto complete
 * @param {Array<IActivity>} activities activities
 * @returns {Array<{ label: string; value:  string }>}
 */
export const getOptionWithoutGrouping = (
  activities: Array<IActivity>
): Array<{ label: string; value: string; activityId: number }> => {
  try {
    return activities.map(({ displayName, activityId }: IActivity) => ({
      key: `${displayName}_${activityId}`,
      label: displayName,
      value: `${displayName}_${activityId}`,
      activityId: activityId,
    }));
  } catch (error) {
    return [];
  }
};

/**
 * @function
 * @description get options with grouping for auto complete
 * @param {Array<IActivity>} activities activities
 * @returns {Array<IGroupingOptions>} grouping option
 */
export const getOptionWithGrouping = (
  activities: Array<IActivity>,
  groupingProperty: string = "",
  isEventGrouping?: boolean
): Array<IGroupingOptions> => {
  let options: Array<IGroupingOptions> = [];
  try {
    if (isEventGrouping) {
      return [
        {
          label: "activities",
          groupingOptions: activities?.filter(
            ({ type }) => trimText(type) != EVENT_ACTIVITY_TYPE.toLowerCase()
          ),
        },
        {
          label: "events",
          groupingOptions: activities?.filter(
            ({ type }) => trimText(type) == EVENT_ACTIVITY_TYPE.toLowerCase()
          ),
        },
      ];
    } else {
      Object.values(_.groupBy(activities, groupingProperty)).forEach(
        (groupActivities: Array<IActivity>) => {
          if (groupActivities)
            options.push({
              label: groupActivities[0].activityType ?? "",
              groupingOptions: groupActivities,
            });
        }
      );
      return options;
    }
  } catch (error) {
    return options;
  }
};

/**
 * @function
 * @description set selected activity Display Name
 * @param {string} selectedActivityModifiedDisplayName - modified Display Name
 * @param {boolean} isActivityOptionWithCode
 * @returns  {string} return the selected Display Name
 */
export const getSelectedActivityDisplayName = (
  selectedActivityModifiedDisplayName: string,
  isActivityOptionWithCode: boolean
) => {
  try {
    let displayName = "";

    if (isActivityOptionWithCode) {
      const lastIndex = selectedActivityModifiedDisplayName.lastIndexOf("_");
      const secondToLastIndex = selectedActivityModifiedDisplayName.lastIndexOf(
        "_",
        lastIndex - 1
      );

      displayName =
        secondToLastIndex !== -1
          ? selectedActivityModifiedDisplayName.substring(0, secondToLastIndex)
          : selectedActivityModifiedDisplayName;
    } else {
      const lastIndex = selectedActivityModifiedDisplayName.lastIndexOf("_");

      displayName =
        lastIndex !== -1
          ? selectedActivityModifiedDisplayName.substring(0, lastIndex)
          : selectedActivityModifiedDisplayName;
    }
    return displayName;
  } catch (error) {
    return "";
  }
};

/**
 * @function
 * @description Determines if the pop confirm title should be shown based on activities and address status
 * @param {Array<any>} activities - List of activities
 * @param {boolean} isAddressUnknown - Flag indicating if the address is unknown
 * @param {number} activityId - Selected activity ID
 * @returns {boolean} True if any activity has categoryName "PrintDocument" and address is unknown, otherwise false
 */
export const getAddressUnknownStatus = (
  activities: Array<any>,
  isAddressUnknown: boolean,
  activityId: number
): boolean => {
  try {
    return (
      activities.some(
        (activity) =>
          activity.categoryName === ActivityCategories.PRINT_DOCUMENT &&
          activityId == activity.activityId
      ) && isAddressUnknown
    );
  } catch (error) {
    return false;
  }
};

/**
 * Retrieves the execution schedule for an activity based on its details and configuration.
 *
 * @param values - An object containing details about the activity. Expected properties:
 *   - `isEdit` (boolean): Indicates if the activity is in edit mode.
 *   - `scheduledTime` (any): The scheduled time for the activity.
 *   - `recordType` (string): The type of record (e.g., "executed").
 *   - `values` (any): Additional activity details.
 * @param isEnableDropDown - A boolean flag indicating whether the dropdown is enabled.
 *   Defaults to `false`.
 * @returns An object containing:
 *   - `executingDateTime` (any): The execution date and time.
 *   - `executingDateTimeWithParameter` (any): The execution date and time with parameters.
 *
 * If the activity is in edit mode and the record type is not "executed", the function
 * returns the scheduled time unless the dropdown is enabled, in which case it returns
 * default values. If the activity exists, the current time is returned. In case of an
 * error, default values are returned.
 */
export const getExecutionSchedule = (
  values: any = {},
  isEnableDropDown: boolean = false
): {
  executingDateTime: any;
  executingDateTimeWithParameter: any;
} => {
  const defaultSchedule = {
    executingDateTime: "",
    executingDateTimeWithParameter: "",
  };

  try {
    const { isEdit, scheduledTime, recordType, activity } = values;

    if (isEdit && recordType !== "executed") {
      const time = dayjs(scheduledTime);

      return isEnableDropDown
        ? defaultSchedule
        : {
            executingDateTime: time,
            executingDateTimeWithParameter: time,
          };
    }

    if (activity) {
      const currentTime = dayjs().format();
      return {
        executingDateTime: currentTime,
        executingDateTimeWithParameter: currentTime,
      };
    }

    return defaultSchedule;
  } catch (error) {
    console.error("Error in setScheduleDateTime:", error);
    return defaultSchedule;
  }
};
